import { Button } from '@/components/ui/button'
import { DataTableColumnHeader } from '@/components/ui/data-table'
import {
  Table,
  TableBody,
  TableCell,
  TableErrorRow,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeletonRows,
} from '@/components/ui/table'
import { useMetaDataScoreCardsQuery } from '@/hooks/api/use-datagovernance-dashboard-query'
import { usePersistentSorting } from '@/hooks/use-persistent-sorting'
import { cn } from '@/lib/utils'
import {
  ColumnDef,
  ColumnFiltersState,
  FilterFn,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { T } from '@tolgee/react'
import { Info } from 'lucide-react'
import React from 'react'
import { FiltersType } from '../..'
import { useDataCatalogNavigation } from '../../../_common/hooks/use-data-catalog-breadcrumbs'
import { extractMetrics, getStyles } from '../../utils'
import { AssessmentDetails } from '../_common/assessment-details'
import { DimensionColumnFilter } from '../_common/dimension-filter'
import { ScoreCardsUpdate } from '../_common/scorecards-update'

export type MetadataTableDef = {
  metric: string
  score: number
  dimension: string
  description: string
  purpose: string
}

const METADATA_ASSESSMENT_FILTERS = [
  {
    label: 'Completeness',
    value: 'Completeness',
  },
  {
    label: 'Ownership',
    value: 'Ownership',
  },
  {
    label: 'Sensitivity & Risk',
    value: 'Sensitivity & Risk',
  },
]

const multiValueFilter: FilterFn<any> = (row, columnId, filterValue) => {
  const cellValue = row.getValue(columnId)
  if (!filterValue || filterValue.length === 0) return true
  return filterValue.includes(cellValue)
}

const METADATA_METRICS_COLUMN_DEF: ColumnDef<MetadataTableDef>[] = [
  {
    enableColumnFilter: false,
    enableSorting: false,
    accessorKey: 'metric',
    size: 50,
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        titleKey='dataGovernanceDashboard.metricsTable.headers.metric'
      >
        <T keyName='dataGovernanceDashboard.metricsTable.headers.metric' />
      </DataTableColumnHeader>
    ),
    cell: ({ cell }) => {
      const value = cell.getValue<string | null>()
      return <p className='text-sm font-medium'>{value}</p>
    },
  },
  {
    enableColumnFilter: false,
    accessorKey: 'score',
    size: 50,
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        titleKey='dataGovernanceDashboard.metricsTable.headers.score'
        columnSort={{
          tooltip: {
            default: 'dataGovernanceDashboard.metricsTable.sort.tooltip.score.default',
            desc: 'dataGovernanceDashboard.metricsTable.sort.tooltip.score.desc',
          },
        }}
      >
        <T keyName='dataGovernanceDashboard.metricsTable.headers.score' />
      </DataTableColumnHeader>
    ),
    cell: ({ cell }) => {
      const value = cell.getValue<string | null>()
      return (
        <p className={cn('text-sm font-semibold', getStyles(value ? +value : 0).text)}>
          {Number.parseFloat(value || '0').toFixed()}%
        </p>
      )
    },
  },
  {
    accessorKey: 'dimension',
    enableSorting: false,
    filterFn: multiValueFilter,
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        titleKey='dataGovernanceDashboard.metricsTable.headers.dimension'
        FilterComp={(props) => (
          <DimensionColumnFilter {...props} filterOptions={METADATA_ASSESSMENT_FILTERS} />
        )}
      >
        <T keyName='dataGovernanceDashboard.metricsTable.headers.dimension' />
      </DataTableColumnHeader>
    ),
    cell: ({ cell }) => {
      const value = cell.getValue<string | null>()
      return (
        <p className='text-sm font-normal'>
          {String(value).charAt(0).toUpperCase() + String(value).slice(1)}
        </p>
      )
    },
  },
  {
    enableColumnFilter: false,
    enableSorting: false,
    accessorKey: 'description',
    size: 250,
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        titleKey='dataGovernanceDashboard.metricsTable.headers.description'
      >
        <T keyName='dataGovernanceDashboard.metricsTable.headers.description' />
      </DataTableColumnHeader>
    ),
    cell: ({ cell }) => {
      const value = cell.getValue<string | null>()
      return <p className='text-sm font-normal'>{value}</p>
    },
  },
  {
    enableColumnFilter: false,
    enableSorting: false,
    accessorKey: 'purpose',
    size: 250,
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        titleKey='dataGovernanceDashboard.metricsTable.headers.purpose'
      >
        <T keyName='dataGovernanceDashboard.metricsTable.headers.purpose' />
      </DataTableColumnHeader>
    ),
    cell: ({ cell }) => {
      const value = cell.getValue<string | null>()
      return <p className='text-sm font-normal'>{value}</p>
    },
  },
]

export function MetadataView({ filters }: { filters: FiltersType }) {
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([])
  const [sorting, setSorting] = usePersistentSorting({
    key: 'dg-dashboard-metadata',
  })
  const { openIndustryReferenceTab } = useDataCatalogNavigation()

  const memoizedFilters = React.useMemo(() => filters, [JSON.stringify(filters)])

  const metaDataScoreCardQuery = useMetaDataScoreCardsQuery({
    filters: memoizedFilters,
    queryOptions: {
      enabled: !!memoizedFilters,
    },
  })

  const {
    completeness,
    ownership,
    sensitivity,
    score = 0,
    metadata,
  } = metaDataScoreCardQuery?.data || {}
  const scoreCardsData = React.useMemo(
    () => [
      { title: 'Completeness', ...completeness },
      { title: 'Ownership', ...ownership },
      { title: 'Sensitivity & Risks', ...sensitivity },
    ],
    [JSON.stringify(metaDataScoreCardQuery)],
  )

  const tableData = React.useMemo(() => {
    if (metaDataScoreCardQuery && Object.keys(metaDataScoreCardQuery)) {
      const modifiedData = metaDataScoreCardQuery.data
        ? extractMetrics(metaDataScoreCardQuery.data)
        : []
      return modifiedData
    }
    return []
  }, [JSON.stringify(metaDataScoreCardQuery)])

  const table = useReactTable<MetadataTableDef>({
    data: tableData,
    columns: METADATA_METRICS_COLUMN_DEF,
    getCoreRowModel: getCoreRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      columnFilters,
      sorting,
    },
  })

  const { rows } = table.getRowModel()
  const isInitialLoading = metaDataScoreCardQuery.status === 'pending'
  const isTableLoading = isInitialLoading && metaDataScoreCardQuery.isFetching && rows.length === 0

  return (
    <>
      <AssessmentDetails
        metaData={metadata}
        issuePercentage={score}
        totalIssues={131}
        scoreCards={scoreCardsData}
        isLoading={
          metaDataScoreCardQuery.status === 'pending' &&
          metaDataScoreCardQuery.isFetching &&
          rows.length === 0
        }
        view='metaData'
        error={metaDataScoreCardQuery.error}
      />
      {metaDataScoreCardQuery.isFetching && <ScoreCardsUpdate message='Updating Metadata Scores' />}
      <div className='my-6'>
        <h2 className='py-3 font-semibold text-gray-900'>Metadata Assessment Metrics</h2>
        <div className='flex items-center justify-between gap-2'>
          <p className='text-sm text-gray-600'>{`Showing ${tableData.length} metrics`}</p>
          <Button
            unstyled
            className='flex items-center gap-1 text-sm font-light hover:text-neutral-300'
            onClick={openIndustryReferenceTab}
          >
            <Info className='size-4' />
            Industry Reference
          </Button>
        </div>
        <div className='mt-8'>
          <Table>
            <TableHeader className={cn('bg-content z-10 bg-neutral-25')}>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      colSpan={header.colSpan}
                      style={{ width: `${header.getSize()}px` }}
                      className={cn(header.id === 'options' && 'text-center')}
                      data-state={
                        (header.column.getIsSorted() || header.column.getIsFiltered()) && 'selected'
                      }
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isTableLoading && (
                <TableSkeletonRows
                  cellCount={METADATA_METRICS_COLUMN_DEF.length}
                  className='bg-neutral-white'
                />
              )}
              {metaDataScoreCardQuery.status === 'error' && (
                <TableErrorRow colSpan={METADATA_METRICS_COLUMN_DEF.length} />
              )}
              {metaDataScoreCardQuery.status === 'success' &&
                rows.length > 0 &&
                rows.map((row) => (
                  <TableRow key={row.id} className='h-[72px] bg-neutral-white'>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        style={{ width: `${cell.column.getSize()}px` }}
                        data-state={
                          (cell.column.getIsSorted() || cell.column.getIsFiltered()) && 'selected'
                        }
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              {metaDataScoreCardQuery.status === 'success' &&
                rows?.length === 0 &&
                !metaDataScoreCardQuery.isFetching && (
                  <TableRow>
                    <TableCell
                      colSpan={METADATA_METRICS_COLUMN_DEF.length}
                      className='h-24 text-center'
                    >
                      No results.
                    </TableCell>
                  </TableRow>
                )}
            </TableBody>
          </Table>
        </div>
      </div>
    </>
  )
}
